import { Injectable } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ApprovalNotification } from '../models/voucher-approval.types';

/**
 * Centralized notification handler service
 * Routes notifications from Laravel Echo to appropriate module streams
 *
 * Usage:
 * 1. AppComponent subscribes to EchoService user notifications
 * 2. All notifications are passed to this service via handleNotification()
 * 3. Components subscribe to specific module observables (e.g., onMaintenanceUpdate$)
 */
@Injectable({
  providedIn: 'root'
})
export class NotificationHandlerService {

  // Main notification stream (all notifications flow through here)
  private notificationStream = new Subject<any>();

  // Module-specific streams
  private maintenanceStream = new Subject<any>();
  private contractsStream = new Subject<any>();
  private tasksStream = new Subject<any>();
  private projectsStream = new Subject<any>();
  private payrollStream = new Subject<any>();
  private attendanceStream = new Subject<any>();
  private employeeStream = new Subject<any>();
  private accountingStream = new Subject<any>();
  private bookingStream = new Subject<any>();
  private notesStream = new Subject<any>();
  private approvalsStream = new Subject<ApprovalNotification>();

  constructor() {
    // Route notifications to appropriate module streams
    this.notificationStream.subscribe(notification => {
      this.routeNotification(notification);
    });
  }

  /**
   * Handle incoming notification from Echo
   * This is called by AppComponent when a notification is received
   */
  handleNotification(notification: any): void {
    this.notificationStream.next(notification);
  }

  /**
   * Route notification to appropriate module stream based on module field
   */
  private routeNotification(notification: any): void {
    if (!notification || !notification.module) {
      console.warn('Notification missing module field:', notification);
      return;
    }

    const module = notification.module.toLowerCase();

    switch (module) {
      case 'maintenance':
        this.maintenanceStream.next(notification);
        break;
      case 'contracts':
        this.contractsStream.next(notification);
        break;
      case 'tasks':
        this.tasksStream.next(notification);
        break;
      case 'projects':
        this.projectsStream.next(notification);
        break;
      case 'payroll':
        this.payrollStream.next(notification);
        break;
      case 'attendance':
        this.attendanceStream.next(notification);
        break;
      case 'employee':
        this.employeeStream.next(notification);
        break;
      case 'accounting':
        this.accountingStream.next(notification);
        break;
      case 'booking':
        this.bookingStream.next(notification);
        break;
      case 'notes':
        this.notesStream.next(notification);
        break;
      case 'approvals':
        this.approvalsStream.next(notification as ApprovalNotification);
        break;
      default:
        console.warn(`Unknown notification module: ${notification.module}`, notification);
    }
  }

  /**
   * Observable for all notifications (useful for global handlers like toast notifications)
   */
  get onNotification$(): Observable<any> {
    return this.notificationStream.asObservable();
  }

  /**
   * Observable for Maintenance module notifications
   * Example notification:
   * {
   *   module: 'Maintenance',
   *   action: 'timeline_updated',
   *   title: 'Timeline Updated',
   *   message: 'Timeline updated for request #123',
   *   service_request_id: 123
   * }
   */
  get onMaintenanceUpdate$(): Observable<any> {
    return this.maintenanceStream.asObservable();
  }

  /**
   * Observable for Contracts module notifications
   */
  get onContractsUpdate$(): Observable<any> {
    return this.contractsStream.asObservable();
  }

  /**
   * Observable for Tasks module notifications
   */
  get onTasksUpdate$(): Observable<any> {
    return this.tasksStream.asObservable();
  }

  /**
   * Observable for Projects module notifications
   */
  get onProjectsUpdate$(): Observable<any> {
    return this.projectsStream.asObservable();
  }

  /**
   * Observable for Payroll module notifications
   */
  get onPayrollUpdate$(): Observable<any> {
    return this.payrollStream.asObservable();
  }

  /**
   * Observable for Attendance module notifications
   */
  get onAttendanceUpdate$(): Observable<any> {
    return this.attendanceStream.asObservable();
  }

  /**
   * Observable for Employee module notifications
   */
  get onEmployeeUpdate$(): Observable<any> {
    return this.employeeStream.asObservable();
  }

  /**
   * Observable for Accounting module notifications
   */
  get onAccountingUpdate$(): Observable<any> {
    return this.accountingStream.asObservable();
  }

  /**
   * Observable for Booking module notifications
   */
  get onBookingUpdate$(): Observable<any> {
    return this.bookingStream.asObservable();
  }

  /**
   * Observable for Notes module notifications
   * Example notification:
   * {
   *   module: 'Notes',
   *   action: 'note_created', // or note_updated, note_deleted
   *   title: 'Note Created',
   *   message: 'A new note has been created',
   *   note_id: 123
   * }
   */
  get onNotesUpdate$(): Observable<any> {
    return this.notesStream.asObservable();
  }

  /**
   * Observable for Approvals module notifications
   */
  get onApprovalsUpdate$(): Observable<ApprovalNotification> {
    return this.approvalsStream.asObservable();
  }

  /**
   * Filter notifications by specific action
   *
   * Example:
   * this.notificationHandler.onMaintenanceUpdate$
   *   .pipe(this.notificationHandler.filterByAction('timeline_updated'))
   *   .subscribe(notification => {
   *     // Handle timeline updates only
   *   });
   */
  filterByAction(action: string) {
    return filter((notification: any) => notification.action === action);
  }

  /**
   * Filter notifications by multiple actions
   *
   * Example:
   * this.notificationHandler.onMaintenanceUpdate$
   *   .pipe(this.notificationHandler.filterByActions(['timeline_updated', 'status_changed']))
   *   .subscribe(notification => {
   *     // Handle timeline updates and status changes
   *   });
   */
  filterByActions(actions: string[]) {
    return filter((notification: any) => actions.includes(notification.action));
  }
}
